import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, from, of } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { ApiService, HttpMethod } from '../api/api.service';
import { Organization, OrganizationGroup, User } from '../users/users.service';
import { UsersService } from '../users/users.service';
import { Paginated } from '../../models/paginated.model';
import { UserRole } from '../user-role/user-role.service';

/** Response body from POST /organization-groups/switch */
interface SwitchOrgResponse {
  activeOrganizationId: number;
  organization: Organization;
}

/** Response body from POST /organization-groups/switch-group */
export interface SwitchGroupResponse {
  activeGroupId: number;
  activeOrganizationId: number | null;
  group: OrganizationGroup;
  organization: Organization | null;
}

export type InitialContextResolutionType = 'cache' | 'auto' | 'modal' | 'none';

export interface InitialContextResolution {
  resolution: InitialContextResolutionType;
  /** Present on cache-hit. */
  cached?: { groupId: number | null; organizationId: number | null };
  /** Present on auto or modal — super admin groups. */
  groups?: OrganizationGroup[];
  /** Present on auto or modal — supervisor orgs (or super admin org auto-pick). */
  orgs?: Organization[];
}

interface ActiveContextCache {
  groupId: number | null;
  organizationId: number | null;
}

interface CachedList<T> {
  data: T[];
  cachedAt: number;
  inFlight$?: Observable<T[]>;
}

const PICKER_CACHE_TTL_MS = 5 * 60 * 1000;
const PICKER_PAGE_SIZE = 100;

@Injectable({
  providedIn: 'root',
})
export class OrganizationGroupService {
  private readonly activeOrgSubject = new BehaviorSubject<Organization | null>(null);
  readonly activeOrg$ = this.activeOrgSubject.asObservable();

  private readonly activeGroupSubject = new BehaviorSubject<OrganizationGroup | null>(null);
  readonly activeGroup$ = this.activeGroupSubject.asObservable();

  private groupsCache: CachedList<OrganizationGroup> | null = null;
  private readonly orgsByGroupCache = new Map<number, CachedList<Organization>>();
  private accessibleOrgsCache: CachedList<Organization> | null = null;

  /**
   * Fires after the active firm context has actually changed (switchOrg or
   * switchGroup completes). Views that show firm-scoped data subscribe to
   * this and re-fetch / reset their state. Subject (not BehaviorSubject) so
   * subscribers do not get a stale replay on init — they only see real
   * post-switch events.
   */
  private readonly firmContextChangedSubject = new Subject<void>();
  readonly firmContextChanged$ = this.firmContextChangedSubject.asObservable();

  constructor(
    private apiService: ApiService,
    private usersService: UsersService,
  ) {}

  /**
   * Seed active org from /users/me (e.g. cached user) so the switcher matches server context.
   */
  seedActiveOrgFromOrganization(org: Organization | null): void {
    if (org) {
      this.activeOrgSubject.next(org);
    }
  }

  seedActiveGroup(group: OrganizationGroup | null): void {
    this.activeGroupSubject.next(group);
  }

  getAccessibleOrgs(): Observable<Organization[]> {
    return from(
      this.apiService.call<Organization[]>(
        HttpMethod.GET,
        'organization-groups/supervisor/accessible-orgs',
      ),
    );
  }

  /**
   * Cached groups list for pickers (super admin firm picker + My Account org group dropdown).
   * Single in-flight de-dupe + TTL cache so a slow `/organization-groups` call only
   * costs us once per ~5 minutes regardless of how many components ask.
   */
  getCachedGroups(forceRefresh = false): Observable<OrganizationGroup[]> {
    if (!forceRefresh && this.groupsCache) {
      if (this.groupsCache.inFlight$) return this.groupsCache.inFlight$;
      if (Date.now() - this.groupsCache.cachedAt < PICKER_CACHE_TTL_MS) {
        return of(this.groupsCache.data);
      }
    }
    const request$ = this.listGroups(1, PICKER_PAGE_SIZE).pipe(
      map((res) =>
        [...res.data].sort((a, b) => a.GROUP_NAME.localeCompare(b.GROUP_NAME)),
      ),
      tap((sorted) => {
        this.groupsCache = { data: sorted, cachedAt: Date.now() };
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.groupsCache = {
      data: this.groupsCache?.data ?? [],
      cachedAt: this.groupsCache?.cachedAt ?? 0,
      inFlight$: request$,
    };
    return request$;
  }

  /**
   * Cached firm list for a given group via the for-switcher endpoint (bypasses
   * active-context scoping). Shared between firm picker and My Account org dropdown.
   */
  getCachedOrgsForGroup(groupId: number, forceRefresh = false): Observable<Organization[]> {
    const existing = this.orgsByGroupCache.get(groupId);
    if (!forceRefresh && existing) {
      if (existing.inFlight$) return existing.inFlight$;
      if (Date.now() - existing.cachedAt < PICKER_CACHE_TTL_MS) {
        return of(existing.data);
      }
    }
    const request$ = this.listGroupOrganizationsForSwitcher(groupId, 1, PICKER_PAGE_SIZE).pipe(
      map((res) =>
        [...res.data].sort((a, b) =>
          a.ORGANIZATION_NAME.localeCompare(b.ORGANIZATION_NAME),
        ),
      ),
      tap((sorted) => {
        this.orgsByGroupCache.set(groupId, { data: sorted, cachedAt: Date.now() });
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.orgsByGroupCache.set(groupId, {
      data: existing?.data ?? [],
      cachedAt: existing?.cachedAt ?? 0,
      inFlight$: request$,
    });
    return request$;
  }

  /** Cached supervisor accessible orgs list (firm picker + My Account active-firm switcher). */
  getCachedAccessibleOrgs(forceRefresh = false): Observable<Organization[]> {
    if (!forceRefresh && this.accessibleOrgsCache) {
      if (this.accessibleOrgsCache.inFlight$) return this.accessibleOrgsCache.inFlight$;
      if (Date.now() - this.accessibleOrgsCache.cachedAt < PICKER_CACHE_TTL_MS) {
        return of(this.accessibleOrgsCache.data);
      }
    }
    const request$ = this.getAccessibleOrgs().pipe(
      map((orgs) =>
        [...orgs].sort((a, b) =>
          a.ORGANIZATION_NAME.localeCompare(b.ORGANIZATION_NAME),
        ),
      ),
      tap((sorted) => {
        this.accessibleOrgsCache = { data: sorted, cachedAt: Date.now() };
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.accessibleOrgsCache = {
      data: this.accessibleOrgsCache?.data ?? [],
      cachedAt: this.accessibleOrgsCache?.cachedAt ?? 0,
      inFlight$: request$,
    };
    return request$;
  }

  private invalidateGroupsCache(): void {
    this.groupsCache = null;
  }

  private invalidateOrgsForGroupCache(groupId?: number): void {
    if (groupId != null) {
      this.orgsByGroupCache.delete(groupId);
    } else {
      this.orgsByGroupCache.clear();
    }
  }

  private invalidateAccessibleOrgsCache(): void {
    this.accessibleOrgsCache = null;
  }

  switchOrg(organizationId: number): Observable<void> {
    return from(
      this.apiService.call<SwitchOrgResponse>(HttpMethod.POST, 'organization-groups/switch', {
        organizationId,
      }),
    ).pipe(
      tap((res) => {
        this.activeOrgSubject.next(res.organization ?? null);
        const cognitoId = this.usersService.getCachedUserDetails()?.USER?.COGNITO_ID;
        if (cognitoId) {
          this.persistActiveContext(
            cognitoId,
            this.activeGroupSubject.value?.GROUP_ID ?? null,
            res.activeOrganizationId,
          );
        }
        this.firmContextChangedSubject.next();
      }),
      map(() => undefined),
    );
  }

  switchGroup(groupId: number): Observable<SwitchGroupResponse> {
    return from(
      this.apiService.call<SwitchGroupResponse>(
        HttpMethod.POST,
        'organization-groups/switch-group',
        { groupId },
      ),
    ).pipe(
      tap((res) => {
        this.activeGroupSubject.next(res.group);
        this.activeOrgSubject.next(res.organization ?? null);
        const cognitoId = this.usersService.getCachedUserDetails()?.USER?.COGNITO_ID;
        if (cognitoId) {
          this.persistActiveContext(cognitoId, res.activeGroupId, null);
        }
        this.firmContextChangedSubject.next();
      }),
    );
  }

  listGroups(page: number, pageSize: number): Observable<Paginated<OrganizationGroup>> {
    return from(
      this.apiService.call<Paginated<OrganizationGroup>>(
        HttpMethod.GET,
        `organization-groups?page=${page}&pageSize=${pageSize}`,
      ),
    );
  }

  /** For existing callers — not used for super admin picker (use listGroupOrganizationsForSwitcher). */
  listGroupOrganizations(groupId: number, page: number, pageSize: number): Observable<Paginated<Organization>> {
    return from(
      this.apiService.call<Paginated<Organization>>(
        HttpMethod.GET,
        `organization-groups/${groupId}/organizations?page=${page}&pageSize=${pageSize}`,
      ),
    );
  }

  /** Super admin picker only — bypasses active-context scoping. */
  listGroupOrganizationsForSwitcher(
    groupId: number,
    page: number,
    pageSize: number,
  ): Observable<Paginated<Organization>> {
    return from(
      this.apiService.call<Paginated<Organization>>(
        HttpMethod.GET,
        `organization-groups/${groupId}/organizations/for-switcher?page=${page}&pageSize=${pageSize}`,
      ),
    );
  }

  listOrganizationUsers(orgId: number, page: number, pageSize: number): Observable<Paginated<User>> {
    return from(
      this.apiService.call<Paginated<User>>(
        HttpMethod.GET,
        `organizations/${orgId}/users?page=${page}&pageSize=${pageSize}`,
      ),
    );
  }

  /** All organizations visible to caller (paginated). Super admin: full list for pickers. */
  listOrganizations(page: number, pageSize: number): Observable<Paginated<Organization>> {
    return from(
      this.apiService.call<Paginated<Organization>>(
        HttpMethod.GET,
        `organizations?page=${page}&pageSize=${pageSize}`,
      ),
    );
  }

  /** All organizations across all active-org scopes — appends scope=all. Used by OSJ picker. */
  listOrganizationsAll(page: number, pageSize: number): Observable<Paginated<Organization>> {
    return from(
      this.apiService.call<Paginated<Organization>>(
        HttpMethod.GET,
        `organizations?page=${page}&pageSize=${pageSize}&scope=all`,
      ),
    );
  }

  createGroup(name: string, description?: string): Observable<OrganizationGroup> {
    return from(
      this.apiService.call<OrganizationGroup>(HttpMethod.POST, 'organization-groups', {
        name: name.trim(),
        description: description?.trim() || undefined,
      }),
    ).pipe(tap(() => this.invalidateGroupsCache()));
  }

  /** Rename a group. Backend route: PUT /organization-groups/:groupId (SuperAdminOnly). */
  updateGroup(groupId: number, name: string): Observable<OrganizationGroup> {
    return from(
      this.apiService.call<OrganizationGroup>(
        HttpMethod.PUT,
        `organization-groups/${groupId}`,
        { name: name.trim() },
      ),
    ).pipe(tap(() => this.invalidateGroupsCache()));
  }

  deleteGroup(groupId: number): Observable<void> {
    return from(
      this.apiService.call<unknown>(HttpMethod.DELETE, `organization-groups/${groupId}`).then(() => undefined),
    ).pipe(
      tap(() => {
        this.invalidateGroupsCache();
        this.invalidateOrgsForGroupCache(groupId);
      }),
    );
  }

  addOrgToGroup(groupId: number, organizationId: number): Observable<unknown> {
    return from(
      this.apiService.call<unknown>(HttpMethod.POST, `organization-groups/${groupId}/orgs`, {
        organizationId,
      }),
    ).pipe(
      tap(() => {
        this.invalidateOrgsForGroupCache(groupId);
        this.invalidateAccessibleOrgsCache();
      }),
    );
  }

  removeOrgFromGroup(groupId: number, organizationId: number): Observable<unknown> {
    return from(
      this.apiService.call<unknown>(
        HttpMethod.DELETE,
        `organization-groups/${groupId}/orgs/${organizationId}`,
      ),
    ).pipe(
      tap(() => {
        this.invalidateOrgsForGroupCache(groupId);
        this.invalidateAccessibleOrgsCache();
      }),
    );
  }

  assignSupervisorToGroup(groupId: number, userId: number): Observable<unknown> {
    return from(
      this.apiService.call<unknown>(HttpMethod.POST, `organization-groups/${groupId}/supervisors`, {
        userId,
      }),
    );
  }

  createOrganization(organizationName: string): Observable<Organization> {
    return from(
      this.apiService.call<Organization>(HttpMethod.POST, 'organizations', {
        ORGANIZATION_NAME: organizationName.trim(),
      }),
    ).pipe(
      tap(() => {
        this.invalidateOrgsForGroupCache();
        this.invalidateAccessibleOrgsCache();
      }),
    );
  }

  /** Rename an organization. Backend route: PATCH /organizations/:id (SuperAdminOnly). */
  updateOrganization(
    organizationId: number,
    organizationName: string,
  ): Observable<Organization> {
    return from(
      this.apiService.call<Organization>(
        HttpMethod.PATCH,
        `organizations/${organizationId}`,
        { ORGANIZATION_NAME: organizationName.trim() },
      ),
    ).pipe(
      tap(() => {
        this.invalidateOrgsForGroupCache();
        this.invalidateAccessibleOrgsCache();
      }),
    );
  }

  deleteOrganization(organizationId: number): Observable<void> {
    return from(
      this.apiService
        .call<unknown>(HttpMethod.DELETE, `organizations/${organizationId}`)
        .then(() => undefined),
    ).pipe(
      tap(() => {
        this.invalidateOrgsForGroupCache();
        this.invalidateAccessibleOrgsCache();
      }),
    );
  }

  /**
   * Determine initial-context resolution branch for the current user.
   * Called by InitialContextResolverService on app shell mount.
   */
  loadInitialContext(role: UserRole): Observable<InitialContextResolution> {
    // 1. DB-backed active context from the user-detail response.
    // API now guarantees activeOrg/activeGroup are populated whenever the user
    // has any org assignment (persisted active org, otherwise the home org).
    // Seed subjects synchronously so the side nav and firm picker show the
    // right firm immediately, with no extra HTTP round-trips on startup.
    const userDetails = this.usersService.getCachedUserDetails();
    if (userDetails?.activeOrg) {
      this.activeGroupSubject.next(userDetails.activeGroup ?? null);
      this.activeOrgSubject.next(userDetails.activeOrg);
      return of({
        resolution: 'cache' as const,
        cached: {
          groupId: userDetails.activeGroup?.GROUP_ID ?? null,
          organizationId: userDetails.activeOrg.ORGANIZATION_ID,
        },
      });
    }

    // 2. Fallback: home org/group from the user-detail response (older API
    // builds, or edge cases where activeOrg wasn't populated). Seed subjects
    // so the picker preselects the home firm rather than first-alphabetical.
    if (userDetails?.ORGANIZATION) {
      this.activeOrgSubject.next(userDetails.ORGANIZATION);
      this.activeGroupSubject.next(userDetails.ORGANIZATION_GROUP ?? null);
      return of({
        resolution: 'cache' as const,
        cached: {
          groupId: userDetails.ORGANIZATION_GROUP?.GROUP_ID ?? null,
          organizationId: userDetails.ORGANIZATION.ORGANIZATION_ID,
        },
      });
    }

    this.activeGroupSubject.next(userDetails?.activeGroup ?? null);

    if (role === UserRole.SUPER_ADMIN) {
      return this.listGroups(1, 100).pipe(
        map((result) => {
          const groups = [...result.data].sort((a, b) =>
            a.GROUP_NAME.localeCompare(b.GROUP_NAME),
          );
          if (groups.length === 0) return { resolution: 'none' as const };
          if (groups.length === 1) return { resolution: 'auto' as const, groups };
          return { resolution: 'modal' as const, groups };
        }),
      );
    }

    if (role === UserRole.SUPERVISOR) {
      return this.getAccessibleOrgs().pipe(
        map((orgs) => {
          const sorted = [...orgs].sort((a, b) =>
            a.ORGANIZATION_NAME.localeCompare(b.ORGANIZATION_NAME),
          );
          if (sorted.length === 0) return { resolution: 'none' as const };
          if (sorted.length === 1) return { resolution: 'auto' as const, orgs: sorted };
          return { resolution: 'modal' as const, orgs: sorted };
        }),
      );
    }

    return of({ resolution: 'none' });
  }

  persistActiveContext(
    cognitoId: string,
    groupId: number | null,
    organizationId: number | null,
  ): void {
    const key = `saffron.activeContext.${cognitoId}`;
    const value: ActiveContextCache = { groupId, organizationId };
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      console.error('[OrganizationGroupService] Failed to persist active context', e);
    }
  }

  clearActiveContextCache(cognitoId: string): void {
    localStorage.removeItem(`saffron.activeContext.${cognitoId}`);
  }

}
